// Pure, side-effect-free alignment math for the Stage-4 "live selfie in the
// chamber" feature. NO DOM, NO canvas, NO ImageBitmap — so it is unit-testable
// under node:test + tsx. All geometry is expressed in plain numbers.
//
// Coordinate spaces used by callers:
//   - viewBox px      the G2FaceMesh SVG space (viewBox 0 0 200 240)
//   - stage px        the .vc-stage box (272 x 296)
//   - image px        the selfie ImageBitmap pixel space
//   - box px          the .vc-photo element box (== stage px; inset:0)
//
// The decorative G2FaceMesh stays FIXED; we move the photo. So we compute a
// similarity transform that maps the photo's detected eye centers onto the
// mesh's CANONICAL eye centers (in stage/box px), then apply it as a CSS
// matrix() with transform-origin: 0 0.

export interface Point {
  x: number;
  y: number;
}

export interface SimilarityTransform {
  scale: number;
  rotation: number; // radians
  tx: number;
  ty: number;
}

export interface EyeTargets {
  left: Point;
  right: Point;
}

/** Percent insets/size for the mesh SVG within .vc-stage (mirrors widget-components.css
 *  `.vc-stage svg.mesh { inset: 13% 20%; width: 60%; height: 74%; }`). */
export interface MeshPlacement {
  insetTopPct: number;
  insetLeftPct: number;
  widthPct: number;
  heightPct: number;
}

/** A rectangle (in selfie image px) describing the camera guide oval at capture. */
export interface GuideRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Which rung of the fallback ladder applies. */
export type AlignmentRung = 'none' | 'similarity' | 'guide-crop' | 'center-crop';

export interface AlignmentInput {
  hasPhoto: boolean;
  source: 'camera' | 'upload' | null;
  detected: boolean; // eyes were detected
  modelOk: boolean; // detector loaded and ran without error
  hasGuideRect: boolean; // camera guide geometry was captured
}

/** Source-of-truth constants, mirrored from glyphs.tsx (G2FaceMesh viewBox) and
 *  widget-components.css (.vc-stage size + svg.mesh placement). Kept here so the
 *  canonical target stays COMPUTED, never hardcoded. */
export const G2_VIEWBOX = { w: 200, h: 240 } as const;
export const VC_STAGE = { w: 272, h: 296 } as const;
export const G2_MESH_PLACEMENT: MeshPlacement = {
  insetTopPct: 13,
  insetLeftPct: 20,
  widthPct: 60,
  heightPct: 74,
};

/** Average of a set of points (e.g. the 4 polygon points of an eye in G2_PTS). */
export function centroid(points: ReadonlyArray<Point>): Point {
  if (points.length === 0) return { x: 0, y: 0 };
  let sx = 0;
  let sy = 0;
  for (const p of points) {
    sx += p.x;
    sy += p.y;
  }
  return { x: sx / points.length, y: sy / points.length };
}

/**
 * Map the mesh's eye centers (in SVG viewBox px) into .vc-stage px, honoring the
 * SVG default preserveAspectRatio = xMidYMid meet (uniform scale, centered).
 * COMPUTED from constants so it still holds if the mesh placement changes.
 */
export function computeCanonicalEyeTargets(
  stageW: number,
  stageH: number,
  placement: MeshPlacement,
  viewBox: { w: number; h: number },
  eyeLvb: Point,
  eyeRvb: Point,
): EyeTargets {
  const boxLeft = (stageW * placement.insetLeftPct) / 100;
  const boxTop = (stageH * placement.insetTopPct) / 100;
  const boxW = (stageW * placement.widthPct) / 100;
  const boxH = (stageH * placement.heightPct) / 100;

  // xMidYMid meet: the smaller scale fits the whole viewBox inside the box.
  const scale = Math.min(boxW / viewBox.w, boxH / viewBox.h);
  const contentW = viewBox.w * scale;
  const contentH = viewBox.h * scale;
  const originX = boxLeft + (boxW - contentW) / 2;
  const originY = boxTop + (boxH - contentH) / 2;

  const map = (p: Point): Point => ({
    x: originX + p.x * scale,
    y: originY + p.y * scale,
  });

  return { left: map(eyeLvb), right: map(eyeRvb) };
}

/**
 * Where a point in image px renders inside a box px area under CSS
 * `object-fit: cover` (uniform scale to cover, centered). Used to translate
 * detected eye coordinates into the element's box space before transforming.
 */
export function imagePointToCoverBox(
  point: Point,
  imgW: number,
  imgH: number,
  boxW: number,
  boxH: number,
): Point {
  const scale = Math.max(boxW / imgW, boxH / imgH);
  const offX = (boxW - imgW * scale) / 2;
  const offY = (boxH - imgH * scale) / 2;
  return { x: offX + point.x * scale, y: offY + point.y * scale };
}

/**
 * Similarity transform (uniform scale + rotation + translation) that maps the
 * detected eye centers exactly onto the canonical eye centers. Determined
 * exactly by the two point correspondences.
 */
export function eyeCentersToSimilarityTransform(
  detectedL: Point,
  detectedR: Point,
  canonicalL: Point,
  canonicalR: Point,
): SimilarityTransform {
  const dx = detectedR.x - detectedL.x;
  const dy = detectedR.y - detectedL.y;
  const cx = canonicalR.x - canonicalL.x;
  const cy = canonicalR.y - canonicalL.y;

  const dDist = Math.hypot(dx, dy) || 1e-6;
  const cDist = Math.hypot(cx, cy);
  const scale = cDist / dDist;
  const rotation = Math.atan2(cy, cx) - Math.atan2(dy, dx);

  const dMidX = (detectedL.x + detectedR.x) / 2;
  const dMidY = (detectedL.y + detectedR.y) / 2;
  const cMidX = (canonicalL.x + canonicalR.x) / 2;
  const cMidY = (canonicalL.y + canonicalR.y) / 2;

  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);

  // t = cMid - scale * R * dMid
  const tx = cMidX - scale * (cos * dMidX - sin * dMidY);
  const ty = cMidY - scale * (sin * dMidX + cos * dMidY);

  return { scale, rotation, tx, ty };
}

/** CSS matrix(a,b,c,d,e,f) components for a similarity transform (origin 0 0). */
export function similarityTransformToMatrix(
  t: SimilarityTransform,
): [number, number, number, number, number, number] {
  const cos = Math.cos(t.rotation);
  const sin = Math.sin(t.rotation);
  return [t.scale * cos, t.scale * sin, -t.scale * sin, t.scale * cos, t.tx, t.ty];
}

/** Ready-to-assign CSS `transform` string for a similarity transform. */
export function similarityTransformToCss(t: SimilarityTransform): string {
  const m = similarityTransformToMatrix(t);
  return `matrix(${m.map((n) => roundTo(n, 5)).join(', ')})`;
}

function roundTo(n: number, dp: number): number {
  const f = 10 ** dp;
  return Math.round(n * f) / f;
}

/** Horizontal extremes (left/right edge midpoints) of a guide rect, in image px.
 *  Used as the two correspondences for the camera no-ML guide-oval crop. */
export function guideRectHorizontalExtremes(rect: GuideRect): { left: Point; right: Point } {
  const cy = rect.y + rect.h / 2;
  return { left: { x: rect.x, y: cy }, right: { x: rect.x + rect.w, y: cy } };
}

/** Horizontal extremes of the chamber's canonical oval (centered in the stage),
 *  in stage px. `rx` is the oval's horizontal radius (--photo-oval-rx). */
export function canonicalOvalExtremes(
  stageW: number,
  stageH: number,
  rx: number,
): { left: Point; right: Point } {
  const cx = stageW / 2;
  const cy = stageH / 2;
  return { left: { x: cx - rx, y: cy }, right: { x: cx + rx, y: cy } };
}

/**
 * Fallback-ladder selector. Pure decision over input flags:
 *   detection success            -> 'similarity'
 *   camera + captured guide rect  -> 'guide-crop'
 *   any other photo case          -> 'center-crop'
 *   no photo (skipped)            -> 'none'
 */
export function selectAlignment(input: AlignmentInput): AlignmentRung {
  if (!input.hasPhoto) return 'none';
  if (input.detected && input.modelOk) return 'similarity';
  if (input.source === 'camera' && input.hasGuideRect) return 'guide-crop';
  return 'center-crop';
}
